import { useState, useEffect, useRef } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { useDocumentHead } from '@/hooks/use-document-head'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ArrowLeft, Monitor, Trash2 } from 'lucide-react'
import { useHeaderExtra } from '@/contexts/header.context'
import { useTheme } from '@/contexts/theme.context'
import { FloatingActionIslandProvider } from '@/contexts/FloatingActionIslandContext'
import { FloatingActionIsland } from '@/components/ui/FloatingActionIsland'
import { findInvalidActionNode } from '@/features/node-agents/findInvalidActionNode'
import { findInvalidTransferNode } from '@/features/node-agents/findInvalidTransferNode'
import { useGraphsClient } from '@/features/node-agents/use-graphs-client'
import { useKnowledgeBases } from '@/features/node-agents/use-knowledge-bases'
import { useRoutingOptions } from '@/features/node-agents/use-routing-options'
import { ApiError } from '@/lib/api/base-client'
import { actionsApiClient } from '@/lib/api/actions.client'
import { triggersApiClient } from '@/lib/api/triggers.client'
import { actionsCatalogApiClient } from '@/lib/api/actions-catalog.client'
import { livekitClient } from '@/lib/api/livekit.client'
import { pathFor, ROUTE_REGISTRY } from '@/lib/routes'
import {
  ActionType,
  AuthoringContext,
  enrichGraphForDispatch,
  parseTransferActionParameters,
  type DispatchResolvers,
  type GraphRecord,
  type NodeActionGroup,
  type RecipientEntry,
  type SaveNodeActionGroupDto,
  type SaveWorkflowActionDto,
  type TransferActionParameters,
  type WorkflowActionCatalogEntry,
} from '@super/shared-types'
import {
  compileCanvas,
  decompileGraph,
  duplicatedName,
  entryNodeId,
  FlowEditor,
  GraphFrontendComponentAllowlistProvider,
  type CanvasState,
  type FlowEditorHandle,
  type KnowledgeBaseOption,
  type TransferActionReference,
  type TransferDraft,
  type TransferPickerOption,
  type TriggerOption,
} from '@super/node-graph-ui'
import { useAppAuth } from '@/contexts/app-auth.context'
import { DirectoryProvider, useDirectory } from '@/contexts/directory.context'
import { describeAction, searchHaystackForAction } from '@/lib/actions/describe'
import { JEV_FLAG, KNOWN_SCRATCHPAD_PATHS } from '@super/shared-types'
import { useFeatureFlag } from '@/hooks/use-feature-flag'
import {
  CallEndOverlay,
  EventFeed,
  MicPicker,
  MuteButton,
  TranscriptionPanel,
  WebCallRoom,
  useGraphEvents,
  type WebCallSession,
} from '@super/node-call-ui'
import { NodeAgentSaveIsland } from '@/features/node-agents/NodeAgentSaveIsland'
import { NodeAgentActionInspector } from '@/features/node-agents/NodeAgentActionInspector'
import { NodeAgentActionPicker } from '@/features/node-agents/NodeAgentActionPicker'
import { TransferActionInlineForm } from '@/components/actions/TransferActionInlineForm'
import { AVAILABLE_VARIABLES_FOR_EDITOR } from '@/features/node-agents/variable-editor-variables'

/** Flatten a catalog entry into the TransferActionReference shape the
 *  FlowEditor's inspector reads. v2 and v3 rows go through this helper so the
 *  editor does not care about the parentage. Catalog rows are 'action'
 *  source; the group variant lives in the useEffect below. */
function transferReferenceFromCatalog(
  entry: WorkflowActionCatalogEntry,
): TransferActionReference {
  const parsed = parseTransferActionParameters({
    actionType: entry.actionType,
    parameters: entry.parameters,
  })
  return {
    workflowActionId: entry.id,
    source: 'action',
    name: entry.resolvedName ?? entry.name ?? null,
    description: entry.description,
    parameters: parsed,
  }
}

/** Short human-readable summary of the first recipient's dial target, used
 *  under the row name in the Transfer picker so two "Transfer to leasing"
 *  rows are tellable apart. */
function dialSummaryOf(entry: WorkflowActionCatalogEntry): string | null {
  const parsed = parseTransferActionParameters({
    actionType: entry.actionType,
    parameters: entry.parameters,
  })
  if (!parsed || parsed.recipients.length === 0) return null
  const first = parsed.recipients[0]
  if (!first) return null
  const dial = first.extension
    ? `${first.number} x${first.extension}`
    : first.number
  const more =
    parsed.recipients.length > 1
      ? ` + ${parsed.recipients.length - 1} more`
      : ''
  return `${dial}${more}`
}

/**
 * Prefer the backend's issue list over the bare message. The API reports two
 * kinds: the contract's schema returns strings, and the reference check
 * returns objects carrying the node or edge each is about.
 */
function saveErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const body = error.body as {
      message?: string
      // GlobalExceptionFilter forwards `details` on 4xx; strings for zod
      // issues, objects (with ref) for save-time validation.
      details?: (string | { message?: string })[]
    } | null
    if (body?.details?.length) {
      const detail = body.details
        .slice(0, 3)
        .map(d => (typeof d === 'string' ? d : d.message))
        .filter(Boolean)
        .join(' · ')
      return `${body.message ?? 'Invalid graph'}: ${detail}`
    }
    return error.message
  }
  return error instanceof Error ? error.message : 'Failed to save the graph'
}

export function NodeAgentPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const client = useGraphsClient()
  const { resolvedTheme } = useTheme()
  const { isSuperAdmin, currentCompany } = useAppAuth()
  const { isEnabled } = useFeatureFlag()
  const authoringContext = isSuperAdmin
    ? AuthoringContext.SUPER_ADMIN
    : AuthoringContext.CUSTOMER_ADMIN
  const editorRef = useRef<FlowEditorHandle>(null)

  const [record, setRecord] = useState<GraphRecord | null>(null)
  const [initialCanvas, setInitialCanvas] = useState<CanvasState | null>(null)
  const [initialLayoutMetadata, setInitialLayoutMetadata] = useState<{
    nodes?: Record<string, { x: number; y: number }>
    edges?: Record<string, { x: number; y: number } | null>
  }>({})
  const [name, setName] = useState('')
  const [active, setActive] = useState(true)
  const [dirty, setDirty] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [webSession, setWebSession] = useState<WebCallSession | null>(null)
  const [webCallStarting, setWebCallStarting] = useState(false)
  const [historyId, setHistoryId] = useState<number | null>(null)
  const knowledgeBases = useKnowledgeBases()
  /** Transfer actions with an in-flight edit draft. Hoisted here so the
   *  graph save gate can read it without a cross-component ref. */
  const [dirtyTransferActionIds, setDirtyTransferActionIds] = useState<
    ReadonlySet<string>
  >(() => new Set())
  /** Resolvers for enrichGraphForDispatch, populated by EditorSurface as the
   *  references map loads. Owned here because startWebCall lives at this
   *  level but the data lives inside EditorSurface. */
  const dispatchResolversRef = useRef<DispatchResolvers>({})
  /** Snapshot of the loaded action groups keyed by id, kept up-to-date by
   *  EditorSurface. handleSave reads from it to validate attached groups
   *  against the catalog form rules before saving the graph. */
  const nodeActionGroupsRef = useRef<
    Record<string, NodeActionGroup | null | undefined>
  >({})
  /** Mirrored copy of EditorSurface's `actionInspectorState` so handleSave
   *  can call the inspector's `validate()` without lifting the entire state
   *  up a level. */
  const actionInspectorStateRef = useRef<{
    isDirty: boolean
    saving: boolean
    save: () => void
    validate: () => boolean
  }>({
    isDirty: false,
    saving: false,
    save: () => {},
    validate: () => true,
  })
  /** The currently-open transfer edit's validate function, or null when no
   *  transfer edit is on screen. Mirrors the action inspector lift. */
  const transferEditingValidateRef = useRef<(() => boolean) | null>(null)
  /** Snapshot of the resolved transfer references, kept up-to-date by
   *  EditorSurface. handleSave reads from it to validate attached transfer
   *  rows against the catalog form rules before saving the graph. */
  const transferReferencesForSaveRef = useRef<
    Record<string, TransferActionReference | null | undefined>
  >({})

  const handleDelete = async () => {
    if (!record) return
    setDeleting(true)
    try {
      const deleted = await client.remove(record.id)
      if (deleted) {
        toast.success('Node agent deleted')
        navigate(ROUTE_REGISTRY.NODE_AGENTS.path)
      } else {
        toast.error('Failed to delete node agent')
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to delete node agent',
      )
    } finally {
      setDeleting(false)
      setDeleteOpen(false)
    }
  }

  useDocumentHead({
    title: record ? `${record.name} | Node Agents` : 'Node Agents',
  })

  useHeaderExtra(
    <Link
      to={ROUTE_REGISTRY.NODE_AGENTS.path}
      className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-accent transition-colors"
      aria-label="Back to node agents"
    >
      <ArrowLeft className="w-4 h-4 text-muted-foreground" />
    </Link>,
  )

  useEffect(() => {
    const load = async () => {
      if (!id) return
      setLoading(true)
      try {
        const existing = await client.get(id)
        if (!existing) {
          toast.error('Node agent not found')
          navigate(ROUTE_REGISTRY.NODE_AGENTS.path, { replace: true })
          return
        }
        const canvas = decompileGraph(existing)
        if (!canvas.success) {
          toast.error(canvas.message)
          navigate(ROUTE_REGISTRY.NODE_AGENTS.path, { replace: true })
          return
        }
        setRecord(existing)
        setName(existing.name)
        setActive(existing.active)
        setInitialCanvas(canvas.data)
        setInitialLayoutMetadata(canvas.layoutMetadata)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [id, client, navigate])

  const handleSave = async () => {
    if (!record) return
    const state = editorRef.current?.getState()
    if (!state) return
    if (!name.trim()) {
      toast.error('Give the agent a name')
      return
    }
    // Save gate: a Transfer node with an unsaved edit draft blocks the graph
    // save. Jump to the first dirty node so the author resolves it (Save,
    // Cancel) before retrying.
    if (dirtyTransferActionIds.size > 0) {
      const dirtyStep = state.steps.find(
        s =>
          s.type === 'transfer' &&
          dirtyTransferActionIds.has(
            (s as unknown as { workflowActionId: string }).workflowActionId,
          ),
      )
      if (dirtyStep) {
        editorRef.current?.focusNode(dirtyStep.id)
        toast.error(
          'Finish editing the transfer action first — Save or Cancel before saving the graph.',
        )
        return
      }
    }
    const layoutMetadata = editorRef.current?.getLayoutMetadata()
    const compiled = compileCanvas(
      state,
      { name: name.trim(), active },
      layoutMetadata,
    )
    if (!compiled.success) {
      toast.error(compiled.message)
      const firstRef = compiled.issues.find(i => i.ref)?.ref
      if (firstRef) {
        const alreadyOnInvalid =
          editorRef.current?.getSelectedStepId() === firstRef
        if (!alreadyOnInvalid) {
          editorRef.current?.focusNode(firstRef) ||
            editorRef.current?.focusEdge(firstRef)
        }
      }
      return
    }
    // Second pass: every action node points at a NodeActionGroup; the group's
    // rows must pass the same checks the catalog form runs on save. Blocks
    // the graph save and focuses the first invalid node.
    const groupsNow = nodeActionGroupsRef.current
    const invalidActionNodeId = findInvalidActionNode(state, groupsNow)
    if (invalidActionNodeId) {
      const alreadyOnInvalid =
        editorRef.current?.getSelectedStepId() === invalidActionNodeId
      toast.error(
        alreadyOnInvalid
          ? 'This action is missing required fields.'
          : 'An action is missing required fields. Open it to fill the fields and try again.',
      )
      if (alreadyOnInvalid) {
        // Mirror the standalone action detail page's behavior: paint the
        // specific invalid fields red instead of only toasting.
        actionInspectorStateRef.current.validate()
      } else {
        editorRef.current?.focusNode(invalidActionNodeId)
      }
      return
    }
    // Same gate for Transfer nodes: a saved reference with missing name or
    // empty recipients blocks the graph save.
    const invalidTransferNodeId = findInvalidTransferNode(
      state,
      transferReferencesForSaveRef.current,
    )
    if (invalidTransferNodeId) {
      const alreadyOnInvalid =
        editorRef.current?.getSelectedStepId() === invalidTransferNodeId
      toast.error(
        alreadyOnInvalid
          ? 'This transfer is missing required fields.'
          : 'A transfer is missing required fields. Open it to fill the fields and try again.',
      )
      if (alreadyOnInvalid) {
        transferEditingValidateRef.current?.()
      } else {
        editorRef.current?.focusNode(invalidTransferNodeId)
      }
      return
    }
    setSaving(true)
    try {
      const updated = await client.save(record.id, compiled.data)
      if (!updated) {
        toast.error('Failed to save the graph')
        return
      }
      setRecord(updated)
      setDirty(false)
      toast.success('Agent updated')
    } catch (error) {
      toast.error(saveErrorMessage(error))
      focusFirstInvalid(error)
    } finally {
      setSaving(false)
    }
  }

  /** Validation errors from the API carry a `ref` field pointing at the
   *  invalid node or edge. Pull the first one, focus it on the canvas, and
   *  open the inspector so the author lands on the thing they need to fix. */
  function focusFirstInvalid(error: unknown) {
    if (!(error instanceof ApiError)) return
    const body = error.body as {
      details?: Array<string | { ref?: string }>
    } | null
    const firstRef = (body?.details ?? [])
      .map(d => (typeof d === 'string' ? null : d.ref))
      .find((ref): ref is string => Boolean(ref))
    if (!firstRef) return
    // Already on the invalid node — keep the author on their open form.
    if (editorRef.current?.getSelectedStepId() === firstRef) return
    // Try node first; if not a node id, try edge. focusNode returns boolean,
    // so use `||` — `??` would never fall through on `false`.
    const focused =
      editorRef.current?.focusNode(firstRef) ||
      editorRef.current?.focusEdge(firstRef)
    if (!focused) return
  }

  /**
   * Dispatch node-caller in web mode with the canvas as drawn, and join the
   * room. The graph travels with the dispatch rather than being fetched, so
   * this tries unsaved work. A call on the stored graph goes through the
   * dispatch route instead.
   */
  const startWebCall = async () => {
    const state = editorRef.current?.getState()
    if (!state) {
      toast.error('Editor is not ready yet')
      return
    }
    const compiled = compileCanvas(state, {
      name: name.trim() || record?.name || 'Untitled',
      active,
    })
    if (!compiled.success) {
      toast.error(compiled.message)
      return
    }
    // The web-dispatch path hands the graph directly to the worker and
    // bypasses the API's assembled() enrichment that the saved-graph path
    // uses. Attach runtime-only fields (currently just Transfer parameters)
    // from the resolvers the editor populated as its references loaded.
    const enrichedGraph = enrichGraphForDispatch(
      compiled.data,
      dispatchResolversRef.current,
    )
    setWebCallStarting(true)
    try {
      const session = await livekitClient.webDispatch({
        nodeGraph: enrichedGraph,
        companyId: currentCompany?.id,
        graphId: record?.id,
        jev: isEnabled(JEV_FLAG),
      })
      if (!session) {
        toast.error('Failed to start browser call')
        return
      }
      setHistoryId(id => (id ?? 0) + 1)
      editorRef.current?.clearSelection()
      setWebSession(session)
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to start browser call',
      )
    } finally {
      setWebCallStarting(false)
    }
  }

  const leaveWebCall = () => setWebSession(null)

  if (loading) {
    return (
      <div className="h-full pt-16">
        <div className="p-6 text-sm text-muted-foreground">
          Loading node agent…
        </div>
      </div>
    )
  }

  if (!record || !initialCanvas) return null

  return (
    <div className="h-full flex flex-col pt-16">
      <FloatingActionIslandProvider>
        <WebCallRoom
          session={webSession}
          onLeave={leaveWebCall}
          onError={message => toast.error(message)}
        >
          <GraphFrontendComponentAllowlistProvider context={authoringContext}>
            <DirectoryProvider>
              <EditorSurface
                initial={initialCanvas}
                initialLayoutMetadata={initialLayoutMetadata}
                knowledgeBases={knowledgeBases}
                editorRef={editorRef}
                name={name}
                setName={setName}
                active={active}
                setActive={setActive}
                dirty={dirty}
                setDirty={setDirty}
                colorMode={resolvedTheme}
                webSession={webSession}
                webCallStarting={webCallStarting}
                historyId={historyId}
                onStartWebCall={startWebCall}
                onLeaveWebCall={leaveWebCall}
                onOpenDelete={() => setDeleteOpen(true)}
                dispatchResolversRef={dispatchResolversRef}
                nodeActionGroupsRef={nodeActionGroupsRef}
                actionInspectorStateRef={actionInspectorStateRef}
                transferEditingValidateRef={transferEditingValidateRef}
                transferReferencesForSaveRef={transferReferencesForSaveRef}
                onTransferActionDirtyChange={(id, isDirty) =>
                  setDirtyTransferActionIds(prev => {
                    const has = prev.has(id)
                    if (has === isDirty) return prev
                    const next = new Set(prev)
                    if (isDirty) next.add(id)
                    else next.delete(id)
                    return next
                  })
                }
              />
            </DirectoryProvider>
          </GraphFrontendComponentAllowlistProvider>
        </WebCallRoom>

        <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Delete “{record.name}”?</DialogTitle>
              <DialogDescription>
                This permanently deletes the agent and its flow. Calls in
                progress are not affected.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setDeleteOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                disabled={deleting}
                onClick={handleDelete}
              >
                {deleting ? 'Deleting…' : 'Delete agent'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <NodeAgentSaveIsland show={dirty} saving={saving} onSave={handleSave} />
        <FloatingActionIsland />
      </FloatingActionIslandProvider>
    </div>
  )
}

/**
 * Renders inside <WebCallRoom> so LiveKit hooks work both in and out of a
 * live session. One header row for both modes: authoring shows the test-call
 * actions, in-call swaps them for mic / mute / leave. FlowEditor stays
 * mounted throughout; its props change when a session starts.
 */
function EditorSurface({
  initial,
  initialLayoutMetadata,
  knowledgeBases,
  editorRef,
  name,
  setName,
  active,
  setActive,
  dirty,
  setDirty,
  colorMode,
  webSession,
  webCallStarting,
  historyId,
  onStartWebCall,
  onLeaveWebCall,
  onOpenDelete,
  dispatchResolversRef,
  nodeActionGroupsRef,
  actionInspectorStateRef,
  transferEditingValidateRef,
  transferReferencesForSaveRef,
  onTransferActionDirtyChange,
}: {
  initial: CanvasState
  initialLayoutMetadata: {
    nodes?: Record<string, { x: number; y: number }>
    edges?: Record<string, { x: number; y: number } | null>
  }
  knowledgeBases: KnowledgeBaseOption[]
  editorRef: React.RefObject<FlowEditorHandle | null>
  name: string
  setName: (value: string) => void
  active: boolean
  setActive: (value: boolean) => void
  dirty: boolean
  setDirty: (dirty: boolean) => void
  colorMode: 'light' | 'dark'
  webSession: WebCallSession | null
  webCallStarting: boolean
  historyId: number | null
  onStartWebCall: () => void
  onLeaveWebCall: () => void
  onOpenDelete: () => void
  dispatchResolversRef: React.MutableRefObject<DispatchResolvers>
  /** Set by EditorSurface every render; read by the graph save validator
   *  to resolve each action node's referenced group. */
  nodeActionGroupsRef: React.MutableRefObject<
    Record<string, NodeActionGroup | null | undefined>
  >
  /** Mirrored from EditorSurface so handleSave can call the inspector's
   *  validate() to highlight fields when the user is already on the
   *  invalid action's edit page. */
  actionInspectorStateRef: React.MutableRefObject<{
    isDirty: boolean
    saving: boolean
    save: () => void
    validate: () => boolean
  }>
  /** The open transfer edit's validator — same role for the Transfer side. */
  transferEditingValidateRef: React.MutableRefObject<(() => boolean) | null>
  /** Snapshot of resolved transfer references so handleSave can validate
   *  every attached transfer workflow action. */
  transferReferencesForSaveRef: React.MutableRefObject<
    Record<string, TransferActionReference | null | undefined>
  >
  /** Lifted to NodeAgentPage so the graph save gate can read the set. */
  onTransferActionDirtyChange: (
    workflowActionId: string,
    dirty: boolean,
  ) => void
}) {
  const inCall = webSession !== null
  const navigate = useNavigate()
  const { isSuperAdmin, currentCompany } = useAppAuth()
  const { roles, staff } = useDirectory()
  const routingOptions = useRoutingOptions()
  const { activeNodeId, previousNodeId, isFinalized } = useGraphEvents({
    sessionKey: webSession?.room ?? null,
    startNodeId: entryNodeId(initial),
  })

  function describeActionGroup(group: NodeActionGroup): string {
    const row = group.members[0]?.workflowAction
    if (!row) return 'Empty action'
    return describeAction(
      {
        actionType: row.actionType as ActionType,
        actionSubtype: row.actionSubtype ?? null,
        parameters: row.parameters,
        contacts: row.contacts,
      },
      { roles, staff },
    )
  }

  /** Pick a @role / @staff mention label for one group member. Two
   *  strict signals, in order:
   *
   *  1. `sourceRoleMemberId` on the member — it came from a @role fan-out.
   *     Resolve to the owning role; return "@role".
   *
   *  2. A staff contact on the member whose on-file address matches this
   *     row's parameters.{email|number} — the author typed the @staff and
   *     the save fanned the same contacts across every sibling row. The
   *     address match is what distinguishes "this row IS the staff recipient"
   *     from "this row is a literal recipient that happens to share the
   *     group-wide contacts list". Return "@staff".
   *
   *  Literal recipients return `null`, so the panel renders them with the
   *  type icon + their literal address, and the panel's dedup collapses
   *  only consecutive expansions of the same mention. */
  function describeActionMember(
    member: NodeActionGroup['members'][number],
  ): { label: string; mention: boolean; key?: string } | null {
    const roleMemberId = member.workflowAction.sourceRoleMemberId
    if (roleMemberId) {
      for (const role of roles) {
        if (role.members.some(rm => rm.id === roleMemberId)) {
          return {
            label: `@${role.name}`,
            mention: true,
            key: `role:${role.id}`,
          }
        }
      }
    }
    const params = (member.workflowAction.parameters ?? {}) as Record<
      string,
      unknown
    >
    const literalAddress =
      member.workflowAction.actionType === 'EMAIL'
        ? params.email
        : member.workflowAction.actionType === 'SMS' ||
            member.workflowAction.actionType === 'PHONE_CALL'
          ? params.number
          : null
    if (typeof literalAddress === 'string' && literalAddress.length > 0) {
      for (const c of member.workflowAction.contacts ?? []) {
        if (!c.staffId) continue
        const match = staff.find(s => s.id === c.staffId)
        if (!match) continue
        const staffAddress =
          member.workflowAction.actionType === 'EMAIL'
            ? match.email
            : match.phone
        if (staffAddress && staffAddress === literalAddress) {
          return {
            label: `@${match.name}`,
            mention: true,
            key: `staff:${match.id}`,
          }
        }
      }
    }
    return null
  }

  /** Pick a @role / @staff mention label for a transfer recipient. Returns
   *  null when the recipient is a literal dialed number. */
  function describeTransferRecipient(
    recipient: RecipientEntry,
  ): { label: string; mention: boolean; key?: string } | null {
    if (recipient.staffId) {
      const match = staff.find(s => s.id === recipient.staffId)
      if (match) {
        return {
          label: `@${match.name}`,
          mention: true,
          key: `staff:${match.id}`,
        }
      }
    }
    if (recipient.sourceRoleMemberId) {
      for (const role of roles) {
        const member = role.members.find(
          m => m.id === recipient.sourceRoleMemberId,
        )
        if (member) {
          return {
            label: `@${role.name}`,
            mention: true,
            key: `role:${role.id}`,
          }
        }
      }
    }
    return null
  }

  // NodeActionGroups referenced by action nodes on the canvas. The inspector
  // and picker read from this map; `undefined` is loading, `null` is deleted.
  const [nodeActionGroupsById, setNodeActionGroupsById] = useState<
    Record<string, NodeActionGroup | null | undefined>
  >({})
  /** Lifted save state from the inline NodeAgentActionInspector so the shared
   *  edit shell renders the sticky save CTA in its own footer — same chrome
   *  as the Transfer edit shell. */
  const [actionInspectorState, setActionInspectorState] = useState<{
    isDirty: boolean
    saving: boolean
    save: () => void
    validate: () => boolean
  }>({
    isDirty: false,
    saving: false,
    save: () => {},
    validate: () => true,
  })
  // Catalog of all shared (named) groups for this company so the picker
  // modal can list them for linking.
  const [availableActionGroups, setAvailableActionGroups] = useState<
    NodeActionGroup[]
  >([])
  const [availableTriggers, setAvailableTriggers] = useState<TriggerOption[]>(
    [],
  )

  const useAdminScope = isSuperAdmin && currentCompany
  const adminCompanyId = useAdminScope ? currentCompany.id : null

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const groups = adminCompanyId
          ? await actionsApiClient.getActionsForCompanyByAdmin(adminCompanyId)
          : await actionsApiClient.getActions()
        if (cancelled) return
        setAvailableActionGroups(groups ?? [])
      } catch (error) {
        if (cancelled) return
        // An empty catalog and a failed read look the same in the picker.
        toast.error('Could not load the action groups. Reload to try again.')
        console.error('Failed to load action groups', error)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [adminCompanyId])

  // The company's trigger rows. Lead and Collections run through hardcoded
  // handlers rather than rows, so they are absent until seeded.
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const definitions = adminCompanyId
          ? await triggersApiClient.getDefinitionsForCompanyByAdmin(
              adminCompanyId,
            )
          : await triggersApiClient.getDefinitions()
        if (cancelled) return
        setAvailableTriggers(
          (definitions ?? [])
            .filter(
              trigger =>
                trigger.active &&
                (trigger.source === 'WEBHOOK' ||
                  trigger.source === 'INTEGRATION'),
            )
            .map(trigger => ({
              id: trigger.id,
              name: trigger.name,
              eventName: trigger.eventName,
              triggerId: trigger.id,
              phoneVariable: trigger.phoneVariable,
              emailVariable: trigger.emailVariable,
              // Start offers a row per variable, not just the two pointers.
              variables: trigger.variables,
            })),
        )
      } catch (error) {
        if (cancelled) return
        // A failed read looks like a company with no triggers, so say which
        // one happened: reloading fixes it, editing does not.
        toast.error('Could not load the triggers. Reload to try again.')
        console.error('Failed to load trigger definitions', error)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [adminCompanyId])

  // Seed the referenced-groups map from the catalog so a node the picker
  // inserts for an existing shared group renders immediately instead of
  // stalling on "Loading…". Per-id fetches below still refresh anything the
  // graph already references.
  useEffect(() => {
    if (availableActionGroups.length === 0) return
    setNodeActionGroupsById(prev => {
      const next = { ...prev }
      let changed = false
      for (const group of availableActionGroups) {
        if (next[group.id] === undefined) {
          next[group.id] = group
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [availableActionGroups])

  useEffect(() => {
    const refs = Array.from(
      new Set(
        initial.steps
          .filter(s => s.type === 'action')
          .map(
            s =>
              (s as unknown as { nodeActionGroupId: string }).nodeActionGroupId,
          )
          .filter((id): id is string => Boolean(id)),
      ),
    )
    if (refs.length === 0) return
    setNodeActionGroupsById(prev => {
      const next = { ...prev }
      for (const id of refs) if (!(id in next)) next[id] = undefined
      return next
    })
    void (async () => {
      const loaded = await Promise.all(
        refs.map(async id => {
          const group = adminCompanyId
            ? await actionsApiClient.getActionForCompanyByAdmin(
                id,
                adminCompanyId,
              )
            : await actionsApiClient.getActionById(id)
          return [id, group] as const
        }),
      )
      setNodeActionGroupsById(prev => {
        const next = { ...prev }
        for (const [id, group] of loaded) next[id] = group
        return next
      })
    })()
  }, [initial.steps, adminCompanyId])

  // Catalog of TRANSFER WorkflowActions this company can reference from a
  // Transfer node. Loaded once per company; refreshed when the author creates
  // a new transfer from the picker. Separate from `availableActionGroups`
  // because Transfer references a WorkflowAction directly rather than a
  // NodeActionGroup wrapper.
  const [transferCatalog, setTransferCatalog] = useState<
    WorkflowActionCatalogEntry[]
  >([])
  /** Non-TRANSFER catalog entries the author can pick from the action-node
   *  picker. Selecting one creates a single-member NodeActionGroup on the fly. */
  const [actionCatalog, setActionCatalog] = useState<
    WorkflowActionCatalogEntry[]
  >([])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const entries = adminCompanyId
        ? await actionsCatalogApiClient.getActionsForCompanyByAdmin(
            adminCompanyId,
          )
        : await actionsCatalogApiClient.getActions()
      if (cancelled) return
      const all = entries ?? []
      setTransferCatalog(all.filter(e => e.actionType === ActionType.TRANSFER))
      setActionCatalog(all.filter(e => e.actionType !== ActionType.TRANSFER))
    })()
    return () => {
      cancelled = true
    }
  }, [adminCompanyId])

  const availableTransferActions: TransferPickerOption[] = (() => {
    const options: TransferPickerOption[] = transferCatalog.map(entry => ({
      source: 'action' as const,
      workflowActionId: entry.id,
      name: entry.resolvedName ?? entry.name ?? null,
      description: entry.description,
      dialSummary: dialSummaryOf(entry),
      searchHaystack: searchHaystackForAction(entry, { roles, staff }),
    }))
    // Single-TRANSFER-member NodeActionGroups surface in the picker too so
    // the author can target a shared "Transfer to leasing" group they already
    // built in /admin/actions. Mixed groups and multi-member groups are
    // excluded because a Transfer node holds exactly one transfer action.
    for (const group of availableActionGroups) {
      if (!group.name) continue
      if (group.members.length !== 1) continue
      const member = group.members[0]
      if (member.workflowAction.actionType !== ActionType.TRANSFER) continue
      options.push({
        source: 'group',
        workflowActionId: member.workflowAction.id,
        groupId: group.id,
        name: group.name,
        description: group.description,
        dialSummary: dialSummaryOf({
          actionType: ActionType.TRANSFER,
          parameters: member.workflowAction.parameters,
        } as WorkflowActionCatalogEntry),
        searchHaystack: [
          group.name,
          group.description,
          describeAction(
            {
              actionType: ActionType.TRANSFER,
              actionSubtype: member.workflowAction.actionSubtype ?? null,
              parameters: member.workflowAction.parameters,
              contacts: member.workflowAction.contacts,
            },
            { roles, staff },
          ),
        ]
          .filter(Boolean)
          .join(' '),
      })
    }
    return options
  })()

  // Resolved details for every Transfer node reference on the canvas. The
  // inspector reads this to decide whether to show the inline editor (for
  // unnamed rows) or the shared summary + "Edit in catalog" link.
  const [transferActionReferencesById, setTransferActionReferencesById] =
    useState<Record<string, TransferActionReference | null | undefined>>({})

  // Expose the resolver to the parent so startWebCall (which does not sit
  // inside this component's scope) can enrich the carried graph before
  // posting. The ref is read lazily at dispatch time, not on each render.
  useEffect(() => {
    dispatchResolversRef.current = {
      getTransferParameters: workflowActionId =>
        transferActionReferencesById[workflowActionId]?.parameters,
    }
  }, [transferActionReferencesById, dispatchResolversRef])

  // Mirror the loaded action groups into the parent-owned ref so the graph
  // save path can validate each action node's referenced group.
  useEffect(() => {
    nodeActionGroupsRef.current = nodeActionGroupsById
  }, [nodeActionGroupsById, nodeActionGroupsRef])

  // Same mirror pattern for the inspector save state — handleSave reads from
  // the ref to call validate() when the user is already on the invalid node.
  useEffect(() => {
    actionInspectorStateRef.current = actionInspectorState
  }, [actionInspectorState, actionInspectorStateRef])
  useEffect(() => {
    transferReferencesForSaveRef.current = transferActionReferencesById
  }, [transferActionReferencesById, transferReferencesForSaveRef])

  // Seed references from the catalog so a node the picker inserts for an
  // existing shared action renders immediately.
  useEffect(() => {
    if (transferCatalog.length === 0) return
    setTransferActionReferencesById(prev => {
      const next = { ...prev }
      let changed = false
      for (const entry of transferCatalog) {
        if (next[entry.id] === undefined) {
          next[entry.id] = transferReferenceFromCatalog(entry)
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [transferCatalog])

  // Also seed from single-TRANSFER-member NodeActionGroups. The catalog
  // endpoint returns standalone WorkflowActions; it does not surface group
  // members. Without this the inspector shows "Loading…" indefinitely when
  // the author picks a group from the Transfer picker.
  useEffect(() => {
    if (availableActionGroups.length === 0) return
    setTransferActionReferencesById(prev => {
      const next = { ...prev }
      let changed = false
      for (const group of availableActionGroups) {
        if (group.members.length !== 1) continue
        const member = group.members[0]
        if (member.workflowAction.actionType !== ActionType.TRANSFER) continue
        const id = member.workflowAction.id
        if (next[id] !== undefined) continue
        const parsed = parseTransferActionParameters({
          actionType: member.workflowAction.actionType,
          parameters: member.workflowAction.parameters,
        })
        next[id] = {
          workflowActionId: id,
          source: 'group',
          groupId: group.id,
          // Carry the group's name so the inspector shows the shared view
          // (name + "Edit in catalog") instead of the inline editor. "Edit
          // in catalog" lands on the group's detail page via the admin's
          // onNavigateToTransferAction routing.
          name: group.name,
          description: group.description,
          parameters: parsed,
        }
        changed = true
      }
      return changed ? next : prev
    })
  }, [availableActionGroups])

  useEffect(() => {
    const refs = Array.from(
      new Set(
        initial.steps
          .filter(s => s.type === 'transfer')
          .map(
            s =>
              (s as unknown as { workflowActionId: string }).workflowActionId,
          )
          .filter((id): id is string => Boolean(id)),
      ),
    )
    if (refs.length === 0) return
    setTransferActionReferencesById(prev => {
      const next = { ...prev }
      for (const id of refs) if (!(id in next)) next[id] = undefined
      return next
    })
    void (async () => {
      const entries = adminCompanyId
        ? await actionsCatalogApiClient.getActionsByIdsForCompany(
            adminCompanyId,
            refs,
          )
        : await actionsCatalogApiClient.getActionsByIds(refs)
      // null = transient fetch error. Leave refs as `undefined` (loading)
      // so a retry or the next graph save can pick up the state. Falling
      // through to the "missing" branch would paint every Transfer node
      // red for a one-off 500.
      if (entries === null) return
      const found = new Set(entries.map(e => e.id))
      setTransferActionReferencesById(prev => {
        const next = { ...prev }
        for (const entry of entries) {
          next[entry.id] = transferReferenceFromCatalog(entry)
        }
        for (const id of refs) {
          if (!found.has(id)) next[id] = null
        }
        return next
      })
    })()
  }, [initial.steps, adminCompanyId])

  async function handleCreateInlineTransferAction(
    parameters: TransferActionParameters,
  ): Promise<string | null> {
    const dto: SaveWorkflowActionDto = {
      name: null,
      description: null,
      actionType: ActionType.TRANSFER,
      parameters: parameters as unknown as Record<string, unknown>,
    }
    const created = adminCompanyId
      ? await actionsCatalogApiClient.createActionForCompany(
          adminCompanyId,
          dto,
        )
      : await actionsCatalogApiClient.createAction(dto)
    if (!created) {
      toast.error('Could not create the transfer')
      return null
    }
    setTransferCatalog(prev => [...prev, created])
    setTransferActionReferencesById(prev => ({
      ...prev,
      [created.id]: transferReferenceFromCatalog(created),
    }))
    setDirty(true)
    return created.id
  }

  /** Wrap one catalog action in a fresh single-member group so the action
   *  node (which references a group id) can point at an individual action
   *  the author picked. Keeps the catalog row intact — the member is a
   *  reference, not a copy. */
  async function handleCreateGroupFromAction(
    workflowActionId: string,
  ): Promise<NodeActionGroup | null> {
    const entry = actionCatalog.find(e => e.id === workflowActionId)
    const label = entry?.resolvedName ?? entry?.name ?? 'Action'
    const dto: SaveNodeActionGroupDto = {
      name: `${label} (auto-group)`,
      description: null,
      actions: [
        {
          workflowActionId,
          actionType: entry?.actionType ?? ActionType.SMS,
        },
      ],
    }
    const created = adminCompanyId
      ? await actionsApiClient.createActionForCompany(adminCompanyId, dto)
      : await actionsApiClient.createAction(dto)
    if (!created) {
      toast.error('Could not attach the action')
      return null
    }
    setNodeActionGroupsById(prev => ({ ...prev, [created.id]: created }))
    setAvailableActionGroups(prev => [...prev, created])
    setDirty(true)
    return created
  }

  /** "Open in catalog" button on the Transfer inspector. Routes to the
   *  action detail page or the group detail page depending on where the
   *  reference came from. Warns before navigating when there are unsaved
   *  graph changes so an accidental click does not lose work. */
  function handleNavigateToTransferAction(reference: TransferActionReference) {
    const target =
      reference.source === 'group' && reference.groupId
        ? pathFor(ROUTE_REGISTRY.NODE_ACTION_GROUPS_DETAIL.id, {
            id: reference.groupId,
          })
        : pathFor(ROUTE_REGISTRY.NODE_ACTIONS_DETAIL.id, {
            id: reference.workflowActionId,
          })
    window.open(target, '_blank', 'noopener,noreferrer')
  }

  // Dirty state lives at NodeAgentPage (save gate reads it); EditorSurface
  // only forwards the per-node signal up.
  const handleTransferActionDirtyChange = onTransferActionDirtyChange

  async function handleFetchTransferActionUsageCount(
    workflowActionId: string,
  ): Promise<number> {
    const usage = adminCompanyId
      ? await actionsCatalogApiClient.getActionUsageForCompanyByAdmin(
          workflowActionId,
          adminCompanyId,
        )
      : await actionsCatalogApiClient.getActionUsage(workflowActionId)
    // Null = usage endpoint failed or lookup unavailable. Force the
    // Duplicate / Edit-for-all decision modal rather than silently editing
    // in place — this action may be referenced by other graphs.
    return usage?.count ?? 2
  }

  async function handleRefreshTransferAction(
    workflowActionId: string,
  ): Promise<void> {
    const entries = adminCompanyId
      ? await actionsCatalogApiClient.getActionsByIdsForCompany(
          adminCompanyId,
          [workflowActionId],
        )
      : await actionsCatalogApiClient.getActionsByIds([workflowActionId])
    // Transient fetch error → leave the ref alone. A follow-up refresh
    // click or auto-reload clears the state cleanly.
    if (entries === null) return
    const fresh = entries.find(e => e.id === workflowActionId)
    setTransferActionReferencesById(prev => ({
      ...prev,
      [workflowActionId]: fresh ? transferReferenceFromCatalog(fresh) : null,
    }))
    if (fresh) {
      setTransferCatalog(prev =>
        prev.some(e => e.id === workflowActionId)
          ? prev.map(e => (e.id === workflowActionId ? fresh : e))
          : [...prev, fresh],
      )
    }
  }

  async function handleSaveTransferActionParameters(
    workflowActionId: string,
    draft: TransferDraft,
  ): Promise<boolean> {
    const dto: SaveWorkflowActionDto = {
      name: draft.name,
      description: draft.description,
      actionType: ActionType.TRANSFER,
      parameters: draft.parameters as unknown as Record<string, unknown>,
    }
    const catalogEntry = transferCatalog.find(e => e.id === workflowActionId)
    const effectiveCompanyId = catalogEntry?.resolvedCompanyId ?? adminCompanyId
    const updated = effectiveCompanyId
      ? await actionsCatalogApiClient.updateActionForCompany(
          workflowActionId,
          effectiveCompanyId,
          dto,
        )
      : await actionsCatalogApiClient.updateAction(workflowActionId, dto)
    if (!updated) {
      toast.error('Could not save the transfer')
      return false
    }
    setTransferActionReferencesById(prev => ({
      ...prev,
      [workflowActionId]: transferReferenceFromCatalog(updated),
    }))
    setTransferCatalog(prev =>
      prev.map(entry => (entry.id === workflowActionId ? updated : entry)),
    )
    return true
  }

  /** Clone the referenced WorkflowAction into a new catalog row with the
   *  given parameters and return the new id. The panel then swaps the
   *  current node's reference via `onPickExisting(newId)`, which already
   *  flows through the editor's existing pick wiring and sets the graph
   *  dirty flag. Sibling Transfer nodes keep the original reference. */
  async function handleDuplicateTransferActionForNode(
    workflowActionId: string,
    draft: TransferDraft,
  ): Promise<string | null> {
    const existing = transferActionReferencesById[workflowActionId]
    const baseName =
      draft.name ?? duplicatedName(existing?.name) ?? 'Untitled transfer'
    const dto: SaveWorkflowActionDto = {
      name: baseName,
      description: draft.description,
      actionType: ActionType.TRANSFER,
      parameters: draft.parameters as unknown as Record<string, unknown>,
    }
    const created = adminCompanyId
      ? await actionsCatalogApiClient.createActionForCompany(
          adminCompanyId,
          dto,
        )
      : await actionsCatalogApiClient.createAction(dto)
    if (!created) {
      toast.error('Could not duplicate the transfer')
      return null
    }
    setTransferCatalog(prev => [...prev, created])
    setTransferActionReferencesById(prev => ({
      ...prev,
      [created.id]: transferReferenceFromCatalog(created),
    }))
    return created.id
  }

  const pendingParamsTimers = useRef<Record<string, number>>({})

  // Cancel pending debounced PUTs on unmount. The last-typed state is lost,
  // which matches the author's expectation when they navigate away mid-type.
  useEffect(() => {
    const timers = pendingParamsTimers.current
    return () => {
      for (const t of Object.values(timers)) window.clearTimeout(t)
    }
  }, [])

  function handleParametersChange(groupId: string, parameters: unknown) {
    const existing = nodeActionGroupsById[groupId]
    if (!existing) return
    const firstMember = existing.members[0]
    if (!firstMember) return
    const optimistic: NodeActionGroup = {
      ...existing,
      members: [
        {
          ...firstMember,
          workflowAction: {
            ...firstMember.workflowAction,
            parameters,
          },
        },
        ...existing.members.slice(1),
      ],
    }
    setNodeActionGroupsById(prev => ({ ...prev, [groupId]: optimistic }))
    window.clearTimeout(pendingParamsTimers.current[groupId])
    pendingParamsTimers.current[groupId] = window.setTimeout(() => {
      void (async () => {
        const dto: SaveNodeActionGroupDto = {
          name: existing.name,
          description: existing.description,
          actions: [{ actionType: ActionType.TRANSFER, parameters }],
        }
        const updated = adminCompanyId
          ? await actionsApiClient.updateActionForCompany(
              groupId,
              adminCompanyId,
              dto,
            )
          : await actionsApiClient.updateAction(groupId, dto)
        if (!updated) {
          toast.error('Could not save the action')
          const refetch = adminCompanyId
            ? await actionsApiClient.getActionForCompanyByAdmin(
                groupId,
                adminCompanyId,
              )
            : await actionsApiClient.getActionById(groupId)
          if (refetch) {
            setNodeActionGroupsById(prev => ({ ...prev, [groupId]: refetch }))
          }
          return
        }
        setNodeActionGroupsById(prev => ({ ...prev, [groupId]: updated }))
      })()
    }, 500)
  }

  function handleNavigateToAdminAction(groupId: string) {
    navigate(
      pathFor(ROUTE_REGISTRY.NODE_ACTION_GROUPS_DETAIL.id, { id: groupId }),
    )
  }
  // No auto-leave here — the worker publishes `finalized` after the closing
  // line finishes playing out, then deletes the room. The room-delete flows
  // through WebCallRoom's onLeave and clears the session naturally. The
  // overlay stays up during the worker's finalize grace and disappears with
  // the disconnect.

  return (
    <>
      <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b">
        <div className="min-w-0">
          <Input
            value={name}
            onChange={e => {
              setName(e.target.value)
              setDirty(true)
            }}
            className="h-8 font-medium max-w-[300px]"
            aria-label="Agent name"
            disabled={inCall}
          />
          <p className="text-xs text-muted-foreground mt-1">
            The call starts at Start and follows the lines. Graph settings holds
            the persona and the knowledge its nodes read.
          </p>
        </div>
        <div className="flex-1" />
        {inCall ? (
          <>
            <MicPicker />
            <MuteButton />
            <Button
              type="button"
              variant="destructive"
              size="sm"
              className="gap-1.5"
              onClick={onLeaveWebCall}
            >
              Leave
            </Button>
          </>
        ) : (
          <>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              disabled={webCallStarting || dirty}
              onClick={onStartWebCall}
              title={
                dirty
                  ? 'Save the graph before you test it so the call runs the same version the post-call actions use.'
                  : undefined
              }
            >
              <Monitor className="h-3.5 w-3.5" />
              {webCallStarting ? 'Starting…' : 'Test in browser'}
            </Button>
            <div className="flex items-center gap-2 rounded-md border px-3 h-9">
              <Switch
                id="agent-active"
                checked={active}
                onCheckedChange={next => {
                  setActive(next)
                  setDirty(true)
                }}
              />
              <Label htmlFor="agent-active" className="text-xs cursor-pointer">
                {active ? 'Active' : 'Inactive'}
              </Label>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5 text-destructive hover:text-destructive"
              onClick={onOpenDelete}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete agent
            </Button>
          </>
        )}
      </div>

      <div className="relative flex-1 min-h-0">
        <FlowEditor
          ref={editorRef}
          initial={initial}
          knowledgeBases={knowledgeBases}
          onDirty={() => setDirty(true)}
          onNotice={message => toast.info(message)}
          colorMode={colorMode}
          activeNodeId={inCall ? activeNodeId : null}
          previousNodeId={inCall ? previousNodeId : null}
          availableScratchpadPaths={KNOWN_SCRATCHPAD_PATHS}
          routingOptions={routingOptions}
          variables={AVAILABLE_VARIABLES_FOR_EDITOR}
          initialPositions={initialLayoutMetadata.nodes}
          initialEdgeLabelPositions={initialLayoutMetadata.edges}
          nodeActionGroupsById={nodeActionGroupsById}
          availableTriggers={availableTriggers}
          onNodeActionGroupParametersChange={handleParametersChange}
          onNavigateToAdminAction={handleNavigateToAdminAction}
          describeActionGroup={describeActionGroup}
          describeActionMember={describeActionMember}
          describeTransferRecipient={describeTransferRecipient}
          renderActionInspector={ctx => (
            <NodeAgentActionInspector
              nodeActionGroupId={ctx.nodeActionGroupId}
              group={ctx.group}
              isDraft={ctx.isDraft}
              onGroupUpdated={updated =>
                setNodeActionGroupsById(prev => ({
                  ...prev,
                  [updated.id]: updated,
                }))
              }
              onGroupCreated={created => {
                setNodeActionGroupsById(prev => ({
                  ...prev,
                  [created.id]: created,
                }))
                setAvailableActionGroups(prev => [...prev, created])
                setDirty(true)
                ctx.replaceGroupId(created.id)
              }}
              replaceGroupId={ctx.replaceGroupId}
              hideSaveRow
              onSaveStateChange={setActionInspectorState}
            />
          )}
          renderActionInspectorFooter={() => (
            <Button
              size="sm"
              onClick={actionInspectorState.save}
              disabled={
                !actionInspectorState.isDirty || actionInspectorState.saving
              }
            >
              {actionInspectorState.saving ? 'Saving…' : 'Save'}
            </Button>
          )}
          availableTransferActions={availableTransferActions}
          transferActionReferencesById={transferActionReferencesById}
          onCreateInlineTransferAction={handleCreateInlineTransferAction}
          onFetchTransferActionUsageCount={handleFetchTransferActionUsageCount}
          onDuplicateTransferActionForNode={
            handleDuplicateTransferActionForNode
          }
          onSaveTransferActionParameters={handleSaveTransferActionParameters}
          onRefreshTransferAction={handleRefreshTransferAction}
          onTransferActionDirtyChange={handleTransferActionDirtyChange}
          onTransferEditingValidateChange={fn => {
            transferEditingValidateRef.current = fn
          }}
          onNavigateToTransferAction={handleNavigateToTransferAction}
          renderTransferEditForm={({ value, onChange, errors }) => (
            <TransferActionInlineForm
              value={value}
              onChange={onChange}
              recipientsError={errors.recipientsError}
            />
          )}
          renderActionPicker={ctx => (
            <NodeAgentActionPicker
              availableGroups={availableActionGroups}
              availableActions={actionCatalog}
              onPickGroup={ctx.onPickGroup}
              onPickAction={async workflowActionId => {
                const created =
                  await handleCreateGroupFromAction(workflowActionId)
                if (created) ctx.onPickGroup(created.id)
              }}
              onStartDraft={ctx.onStartDraft}
            />
          )}
          onFetchActionGroupUsage={async groupId => {
            const usage =
              isSuperAdmin && currentCompany
                ? await actionsApiClient.getActionUsageForCompanyByAdmin(
                    groupId,
                    currentCompany.id,
                  )
                : await actionsApiClient.getActionUsage(groupId)
            return usage?.count ?? 0
          }}
          onDuplicateActionGroup={async groupId => {
            const existing = nodeActionGroupsById[groupId]
            if (!existing) return null
            const dto: SaveNodeActionGroupDto = {
              name: duplicatedName(existing.name),
              description: existing.description ?? null,
              actions: existing.members.map(m => ({
                actionType: m.workflowAction.actionType,
                actionSubtype: m.workflowAction.actionSubtype,
                parameters: m.workflowAction.parameters,
                integrationId: m.workflowAction.integrationId,
                sourceRoleMemberId: m.workflowAction.sourceRoleMemberId,
                contacts: m.workflowAction.contacts,
              })),
            }
            const created =
              isSuperAdmin && currentCompany
                ? await actionsApiClient.createActionForCompany(
                    currentCompany.id,
                    dto,
                  )
                : await actionsApiClient.createAction(dto)
            if (!created) return null
            setNodeActionGroupsById(prev => ({
              ...prev,
              [created.id]: created,
            }))
            setAvailableActionGroups(prev => [...prev, created])
            setDirty(true)
            return created.id
          }}
          onRefreshNodeActionGroup={async groupId => {
            const latest =
              isSuperAdmin && currentCompany
                ? await actionsApiClient.getActionForCompanyByAdmin(
                    groupId,
                    currentCompany.id,
                  )
                : await actionsApiClient.getActionById(groupId)
            setNodeActionGroupsById(prev => ({
              ...prev,
              [groupId]: latest ?? null,
            }))
          }}
          rightPanel={
            historyId !== null ? (
              <div
                key={historyId}
                className="flex h-full min-h-0 flex-col gap-3"
              >
                <div className="min-h-0 flex-1 overflow-hidden">
                  <TranscriptionPanel room={webSession?.room ?? ''} />
                </div>
                <div className="min-h-0 flex-1 overflow-hidden">
                  <EventFeed />
                </div>
              </div>
            ) : undefined
          }
        />
        <CallEndOverlay show={inCall && isFinalized} />
      </div>
    </>
  )
}
